import { useGame } from '../state/useGame';
import { BANK_TABS, BANK_TAB_SLOTS } from '../domain/economy/containers';
import { ItemSlot } from './ItemSlot';
import { TransferToolbar } from './TransferToolbar';

/**
 * Bank: 4 tabs × 16 explicit slots. Placement comes only from state — an
 * item lives on the tab/slot the player put it on. The inventory renders
 * on the same page so bank ↔ inventory moves need no navigation.
 */
export function BankPage() {
  const bank = useGame((s) => s.state.bank);
  const inventory = useGame((s) => s.state.inventory);
  const skills = useGame((s) => s.state.skills);
  const bankTab = useGame((s) => s.state.bankTab);
  const setBankTab = useGame((s) => s.setBankTab);

  const stackCount = bank.filter(Boolean).length;
  const totalItems = bank.reduce((sum, s) => sum + (s?.qty ?? 0), 0);
  const tabStart = bankTab * BANK_TAB_SLOTS;

  return (
    <>
      <h1 className="page-title">Bank</h1>
      <p className="page-subtitle">
        {stackCount} / 64 slots · {totalItems} items banked. Select an item, then pick a
        destination slot — or use the toolbar. Tap, keyboard, and drag all work.
      </p>

      <div className="bank-tabs" role="tablist" aria-label="Bank tabs">
        {Array.from({ length: BANK_TABS }, (_, tab) => {
          const used = bank
            .slice(tab * BANK_TAB_SLOTS, (tab + 1) * BANK_TAB_SLOTS)
            .filter(Boolean).length;
          return (
            <button
              key={tab}
              role="tab"
              className={`bank-tab ${tab === bankTab ? 'active' : ''}`}
              onClick={() => setBankTab(tab)}
              aria-selected={tab === bankTab}
              aria-label={`Tab ${tab + 1}, ${used} occupied ${used === 1 ? 'slot' : 'slots'}`}
            >
              Tab {tab + 1}
              {used > 0 && <span className="badge" aria-hidden="true">{used}</span>}
            </button>
          );
        })}
      </div>

      <div className="bank-grid" role="tabpanel" aria-label={`Bank tab ${bankTab + 1}`}>
        {Array.from({ length: BANK_TAB_SLOTS }, (_, i) => {
          const index = tabStart + i;
          return (
            <ItemSlot
              key={index}
              slotRef={{ container: 'bank', index }}
              stack={bank[index] ?? null}
              label={`Bank tab ${bankTab + 1} slot ${i + 1}`}
            />
          );
        })}
      </div>

      <TransferToolbar />

      <h2 style={{ margin: '18px 0 8px', fontSize: 16 }}>Inventory</h2>
      <div className="bank-grid">
        {inventory.map((stack, i) => (
          <ItemSlot
            key={i}
            slotRef={{ container: 'inventory', index: i }}
            stack={stack ?? null}
            label={`Inventory slot ${i + 1}`}
          />
        ))}
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 16 }}>Skills snapshot</h2>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {Object.entries(skills).map(([id, p]) => (
            <div key={id}>
              <span style={{ color: 'var(--ui-text-dim)' }}>{id.replaceAll('_', ' ')}</span>{' '}
              <strong>Lv {p.level}</strong>{' '}
              <span style={{ color: 'var(--accent-nous)' }}>{p.xp.toLocaleString()} XP</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
